import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import { Phone, MessageSquare, BrainCircuit, ArrowRight, Check, Bot } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SectionHeading } from './HomePrimitives';
import { BrandIcon } from './BrandIcon';
import './memory-timeline.css';

const MOMENTS = [
  { when: '1 month ago', channel: 'Instagram DM', brand: 'instagram', text: 'Do you do teeth whitening?', fact: 'Interested in whitening' },
  { when: '1 week ago', channel: 'SMS', icon: MessageSquare, text: 'Is it OK with sensitive teeth?', fact: 'Has sensitive teeth' },
  { when: 'Yesterday', channel: 'Phone call', icon: Phone, text: 'Can I come in after work?', fact: 'Prefers evenings' },
];
const TODAY = {
  channel: 'WhatsApp', brand: 'whatsapp', text: 'Hi, it’s Maria again!',
  reply: ['Welcome back, Maria! Still thinking about ', 'whitening', '? We have a gentle option for ', 'sensitive teeth', ' — and Thursday at ', '5:30 PM', ' is open after work. Want it?'],
};
const STEP_MS = 1500;
const LAST = MOMENTS.length + 1;

const ChannelPill = ({ m }) => (
  <span className="vx-mem-channel">{m.brand ? <BrandIcon name={m.brand} /> : <m.icon size={13} />}{m.channel}</span>
);

const Moment = ({ m, i, on }) => (
  <div className={`vx-mem-moment${on ? ' is-on' : ''}`} data-testid={`home-memory-moment-${i}`}>
    <span className="vx-mem-node" />
    <p className="vx-mem-when">{m.when}</p>
    <ChannelPill m={m} />
    <AnimatePresence>
      {on && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
          <p className="vx-mem-bubble">{m.text}</p>
          <motion.p className="vx-mem-fact" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 0.35 }}>
            <BrainCircuit size={12} />Remembered: {m.fact}
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const Today = ({ step }) => (
  <div className={`vx-mem-today${step >= MOMENTS.length ? ' is-on' : ''}`} data-testid="home-memory-today">
    <div className="vx-mem-today-head"><span className="vx-mem-node is-today" /><p className="vx-mem-when">Today</p><ChannelPill m={TODAY} /></div>
    <div className="vx-mem-today-body">
      <div className="vx-mem-chat">
        <AnimatePresence>
          {step >= MOMENTS.length && <motion.p key="q" className="vx-mem-bubble is-lead" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>{TODAY.text}</motion.p>}
          {step >= LAST && (
            <motion.div key="a" className="vx-mem-reply" data-testid="home-memory-reply" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
              <span className="vx-mem-reply-avatar"><Bot size={14} /></span>
              <p>{TODAY.reply.map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : <React.Fragment key={i}>{part}</React.Fragment>))}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="vx-mem-card" data-testid="home-memory-card">
        <p className="vx-mem-card-title"><BrainCircuit size={14} />What the AI remembers</p>
        <ul>{MOMENTS.map((m, i) => <li key={m.fact} className={step > i ? 'is-on' : ''}><Check size={13} />{m.fact}<small>{m.when} · {m.channel}</small></li>)}</ul>
      </div>
    </div>
  </div>
);

export const SharedMemory = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.35 });
  const reduce = useReducedMotion();
  const [step, setStep] = useState(reduce ? LAST : -1);

  useEffect(() => {
    if (reduce || !inView) return undefined;
    const t = setTimeout(() => setStep(s => (s >= LAST ? -1 : s + 1)), step >= LAST ? 5200 : step < 0 ? 500 : STEP_MS);
    return () => clearTimeout(t);
  }, [step, inView, reduce]);

  return <section ref={ref} id="shared-memory" className="hm-band ag-memory" data-testid="home-shared-memory-section"><div className="hm-container">
    <SectionHeading id="home-memory" number="03" label="ONE MEMORY" title="Remembers every conversation." accent="A day, a week, a month later." description="Instagram, SMS, calls, WhatsApp, Messenger or web chat — your AI picks up right where the lead left off." dark />
    <div className="vx-mem-stage" data-testid="home-memory-timeline">
      <div className="vx-mem-moments">
        <div className="vx-mem-track"><span className="vx-mem-track-fill" style={{ '--p': Math.max(0, Math.min(1, (step + 1) / MOMENTS.length)) }} /></div>
        {MOMENTS.map((m, i) => <Moment key={m.when} m={m} i={i} on={step >= i} />)}</div>
      <Today step={step} />
    </div>
    <div className="ag-section-foot"><Link className="hm-text-link" to="/products/messaging" data-testid="home-memory-explore">Explore connected messaging<ArrowRight size={16} /></Link></div>
    <p className="vx-footnote" data-testid="home-memory-footnote">Example contact. Memory is kept per client and identified contact; channels depend on connected accounts.</p>
  </div></section>;
};
